import type { Maybe, MaybeUndef } from '@@types/helpers'
import { ensureArray } from '@libs/ensureArray'
import { isDefined, isNotFalsy } from '@libs/isDefined'
import { getLogger } from '@libs/logger'
import type {
  IRouteDefinition,
  IRouteInfo,
  IRouteReversed,
  IRouteSpecs,
  RouteUrlParameters,
  Routes
} from '@libs/Router/types'
import type { Action, Router } from '@remix-run/router'
import type { Location } from 'react-router-dom'
import { get } from 'lodash'
import type QueryString from 'qs'
import { parse as parseQs, stringify as stringifyQs } from 'qs'
import { extractParamsFromUrlPattern } from './functions'

/**
 * Update type compatible with both history v5 and router subscribe.
 * Used to maintain backwards compatibility with existing handlers.
 */
export interface RouterUpdate {
  location: Location
  action: Action
}

export default class AppRouter<
  TRouteName extends string,
  TRouteDefinition extends IRouteDefinition<TRouteName>
> {
  public logger = getLogger('AppRouter')

  /**
   * Proxy functions to the router object.
   * Maintains backwards compatibility with the history API.
   */
  history = {
    location: (): Maybe<Location> => {
      if (!this._isRouterDefined(this.$router)) {
        return null
      }

      return this.$router.state.location
    },

    push: (path: string, state?: unknown): void => {
      if (!this._isRouterDefined(this.$router)) {
        return
      }

      this.$router.navigate(path, { state })
    },

    replace: (path: string, state?: unknown): void => {
      if (!this._isRouterDefined(this.$router)) {
        return
      }

      this.$router.navigate(path, { replace: true, state })
    }
  }

  private $routes: Routes<TRouteDefinition['routeName']>
  private $reversedRoutes: Array<
    IRouteReversed<TRouteDefinition['routeName']>
  > = []

  private $defaultRouteParameters: Maybe<RouteUrlParameters> = null

  private $router: Maybe<Router> = null
  private $routerUnsubscribe: Maybe<() => void> = null
  private $onHistoryChangeHandlers: Array<(update: RouterUpdate) => void> = []

  /**
   * Instanciate a new AppRouter from a Record type of routes.
   */
  constructor(routes: Routes<TRouteName>) {
    this.$routes = routes

    // compute reversed route to be able to retrieve the route name from a pathname
    this.$reversedRoutes = (
      Object.keys(routes) as Array<TRouteDefinition['routeName']>
    )
      .map(routeName => {
        const pathname = routes[routeName].pathname
        const regexpStr = pathname.replace(/:(\w+)/g, '[^/]+')
        const regexp = new RegExp(regexpStr)

        return {
          pathname,
          regexp,
          routeName
        }
      })
      // sort by endpoint length to match the longest route first
      .sort((a, b) => (a.pathname.length < b.pathname.length ? 1 : -1))
  }

  /**
   * Set the router object and binds to router state changes.
   * Guards against multiple calls with the same router to prevent subscription leaks.
   */
  setRouter(router: Router): this {
    // Early return if same router is already set
    if (this.$router === router) {
      return this
    }

    // Unsubscribe from previous router if any
    if (this.$routerUnsubscribe) {
      this.$routerUnsubscribe()
      this.$routerUnsubscribe = null
    }

    this.$router = router

    // Subscribe to router state changes and convert to RouterUpdate format
    this.$routerUnsubscribe = this.$router.subscribe(state => {
      const update: RouterUpdate = {
        location: state.location,
        action: state.historyAction
      }
      this.$onHistoryChangeHandlers.forEach(handler => {
        handler(update)
      })
    })

    return this
  }

  /**
   * @deprecated Use setRouter instead. Kept for backwards compatibility.
   */
  setHistory(router: Router): this {
    return this.setRouter(router)
  }

  /**
   * Bind handler to a router state change event.
   */
  onHistoryChange(handler: (update: RouterUpdate) => void): this {
    this.$onHistoryChangeHandlers.push(handler)
    return this
  }

  /**
   * Set default route parameters used when building routes.
   */
  setRouterDefaultRouteParameters(
    defaultRouteParameters: RouteUrlParameters
  ): this {
    this.$defaultRouteParameters = defaultRouteParameters
    return this
  }

  /**
   * Return the specifications of the routeName retrieved from the
   * current history pathname.
   */
  getCurrentRouteSpecs(): Maybe<IRouteSpecs<TRouteDefinition['routeName']>> {
    const routeName = this.getCurrentRouteName()

    if (!routeName) {
      return null
    }

    return this.$routes[routeName]
  }

  /**
   * Return the specifications of a routeName.
   */
  getRouteSpecs(
    routeName: TRouteDefinition['routeName']
  ): IRouteSpecs<TRouteDefinition['routeName']> {
    return this.$routes[routeName]
  }

  /**
   * Return the pathname of a routeName.
   */
  getRoutePathname(routeName: TRouteDefinition['routeName']): string {
    const routeInfo = this.$routes[routeName]

    if (!routeInfo) {
      this.logger.warn(`Route ${routeName} has not been found.`)
    }

    return routeInfo.pathname
  }

  /**
   * Return the routeName of the current pathname of the router.
   */
  getCurrentRouteName(): Maybe<TRouteDefinition['routeName']> {
    if (!this._isRouterDefined(this.$router)) {
      return null
    }

    const pathname = this.$router.state.location.pathname

    if (!pathname) {
      return null
    }

    return this.getRouteName(pathname)
  }

  /**
   * Return the routeName of the passed pathname.
   */
  getRouteName(pathname: string): Maybe<TRouteDefinition['routeName']> {
    for (let i = 0; i <= this.$reversedRoutes.length - 1; i++) {
      if (this.$reversedRoutes[i].regexp.test(pathname)) {
        return this.$reversedRoutes[i].routeName
      }
    }

    return null
  }

  /**
   * Return parameters of a route.
   *
   * Useful to retrieve url parameters outside the render life cycle
   * that limits `this.props.match.params` to the parameters available relative
   * to the <Route /> component.
   */
  getRouteParameters<T extends TRouteDefinition>(
    route: T | T[],
    routerObject?: Router
  ): Maybe<T['parameters']> {
    const wantedRouter = routerObject || this.$router

    if (!this._isRouterDefined(wantedRouter)) {
      return null
    }

    for (const _route of ensureArray(route)) {
      const patternUrl = this.getRoutePathname(_route.routeName)
      const params = extractParamsFromUrlPattern(
        patternUrl,
        wantedRouter.state.location.pathname
      )

      if (params) {
        return this._castParameters(_route, params)
      }
    }

    return null
  }

  /**
   * Return the query parameters of the current location.
   */
  getCurrentRouteQueryStringParameters<Q>(
    routerObject?: Router
  ): MaybeUndef<Q> {
    const wantedRouter = routerObject || this.$router

    if (!this._isRouterDefined(wantedRouter)) {
      return
    }

    const queryString = wantedRouter.state.location.search

    return parseQs(queryString, {
      allowDots: true,
      ignoreQueryPrefix: true
    }) as unknown as Q
  }

  /**
   * Return a IRouteInfo object from the current route.
   */
  getCurrentRouteInfo<Q>(): IRouteInfo<TRouteName, Q> {
    return {
      routeName: this.getCurrentRouteName(),
      routeParameters: null,
      queryStringParameters: this.getCurrentRouteQueryStringParameters<Q>()
    }
  }

  /**
   * Split a pathname to pathnames, from the shorter to the longuest.
   * Example:
   *
   * /profile/:profileName/management/accounts/users/create
   *
   * =>
   *
   * /profile
   * /profile/:profileName
   * /profile/:profileName/management
   * /profile/:profileName/management/accounts
   * /profile/:profileName/management/accounts/users
   * /profile/:profileName/management/accounts/users/create
   */
  splitPathname(pathname: string): string[] {
    const allPathnames = []

    let newIndex: number
    // add one more slash to catch the last one
    let subPathname = `${pathname}/`

    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    while ((newIndex = subPathname.lastIndexOf('/'))) {
      subPathname = subPathname.slice(0, newIndex)
      allPathnames.push(subPathname)
    }

    return allPathnames.filter(isNotFalsy).reverse()
  }

  /**
   * Return the pathname from routeInfos.
   */
  makeRouteInfosPathname(
    routeDefinition: TRouteDefinition,
    stringifyOptions?: QueryString.IStringifyOptions
  ): string {
    const { routeName, parameters, queryStringParameters } = routeDefinition

    const pathname = this.getRoutePathname(routeName)
    const allParameters = this._getAllRouteParameters(parameters)

    // replace parameters values
    const url = this._buildUrlParams(pathname, allParameters)

    if (!queryStringParameters) {
      return url
    }

    // add querystring parameters
    const queryString = this.makeRouteQueryString(
      queryStringParameters,
      stringifyOptions
    )

    if (!queryString) {
      return url
    }

    return `${url}?${queryString}`
  }

  /**
   * Compute the querystring from the route querystring parameters.
   */
  makeRouteQueryString(
    queryStringParameters: TRouteDefinition['queryStringParameters'],
    stringifyOptions?: QueryString.IStringifyOptions
  ): string {
    return stringifyQs(queryStringParameters, {
      allowDots: true,
      skipNulls: true,
      arrayFormat: 'brackets',
      ...stringifyOptions
    })
  }

  /**
   * Return true if the current route name if one of `routeNames`.
   */
  isCurrentRouteNameIsOneOf(
    routeNames: Array<TRouteDefinition['routeName']>
  ): boolean {
    const currentRouteName = this.getCurrentRouteName()

    if (!currentRouteName) {
      return false
    }

    return routeNames.includes(currentRouteName)
  }

  /**
   * Return true if `pathname` is starting by one of the passed
   * pathnames.
   *
   * Use this method when history is not yet available.
   */
  isPathnameIsStartingByOneOf(pathname: string, pathnames: string[]): boolean {
    return pathnames.some(path => pathname.startsWith(path))
  }

  /**
   * Return true if the current route specs is starting by one of the passed routeSpecs.
   * Using routeSpecs allows to compare pathnames with placeholders.
   *
   * Example:
   *
   * (AppRouteName.IoE_Details) /profile/:profileName/indicators-of-exposure/ad/details/:checkerId-:checkerCodename
   * is starting by
   * (AppRouteName.IoE_Board_AD) /profile/:profileName/indicators-of-exposure/ad
   */
  isCurrentRouteSpecsIsStartingByOneOf<TRouteDefinition>(
    routesSpecs: Array<IRouteSpecs<TRouteDefinition>>
  ): boolean {
    const currentRouteSpecPathname = this.getCurrentRouteSpecs()?.pathname

    if (!currentRouteSpecPathname) {
      return false
    }

    return routesSpecs.some(routeSpecs =>
      currentRouteSpecPathname.startsWith(routeSpecs.pathname)
    )
  }

  /**
   * Perform a "hard redirect" to url.
   */
  hardRedirect(
    routeDefinition: Maybe<TRouteDefinition | string>,
    stringifyOptions?: QueryString.IStringifyOptions
  ): void {
    if (!routeDefinition) {
      window.document.location.reload()
      return
    }

    window.document.location.href =
      typeof routeDefinition === 'string'
        ? routeDefinition
        : this.makeRouteInfosPathname(routeDefinition, stringifyOptions)
  }

  /**
   * Return the router object.
   */
  routerObject(): Maybe<Router> {
    return this.$router
  }

  /**
   * @deprecated Use routerObject instead. Kept for backwards compatibility.
   */
  historyObject(): Maybe<Router> {
    return this.$router
  }

  /**
   * Log registered routes.
   */
  dumpRoutes() {
    const allRouteNames = Object.keys(this.$routes) as Array<
      TRouteDefinition['routeName']
    >

    allRouteNames.forEach(routeName => {
      const routeSpecs = this.$routes[routeName]
      this.logger.info(`- ${routeName}: ${routeSpecs.pathname}`)
    })
  }

  /**
   * Private
   */

  private _isRouterDefined<T>(router: Maybe<T>): router is T {
    if (!router) {
      this.logger.error('Router object is not defined')
      return false
    }

    return true
  }

  private _getAllRouteParameters(
    routeParameters?: Maybe<RouteUrlParameters>
  ): RouteUrlParameters {
    const allParameters: RouteUrlParameters = {
      ...this.$defaultRouteParameters,
      ...routeParameters
    }
    return allParameters
  }

  /**
   * Cast parameters to numbers if declared as a number and if it looks like
   * a number.
   */
  private _castParameters<T extends TRouteDefinition>(
    route: T,
    parameters: {
      [key: string]: string
    }
  ): T['parameters'] {
    const paramTypes = Object.entries(route.parameters).reduce(
      (acc, [param, type]) => {
        acc.set(param, typeof type === 'number' ? 'number' : 'string')
        return acc
      },
      new Map<string, 'string' | 'number'>()
    )

    const finalParams = Object.entries(parameters).reduce<T['parameters']>(
      (acc, [key, value]) => {
        if (!isDefined(value)) {
          return acc
        }

        const isDigit =
          paramTypes.get(key) === 'number' && /^[0-9]+$/.test(value)

        return {
          // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
          ...acc,
          [key]: isDigit ? Number(value) : value
        }
      },
      {}
    )

    return finalParams
  }

  /**
   * Return a pathname with parameters replaced in placeholders.
   */
  private _buildUrlParams(
    pathname: string,
    allParameters: RouteUrlParameters
  ): string {
    let url = pathname.replace(/:(\w+)/g, parameterName => {
      // remove ':' at beginning
      const replacement = get(allParameters, parameterName.slice(1))

      if (replacement === undefined) {
        this.logger.warn(
          `Missing replacement for the parameter "${parameterName}" in the endpoint "${pathname}".`
        )
        return parameterName
      }

      return String(replacement)
    })

    // trim trailing '/'
    if (url !== '/') {
      url = url.replace(/\/*$/, '')
    }

    return encodeURI(url)
  }
}
